import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOTPVerification } from '../hooks/useOTPVerification';
import { useSeller } from '../contexts/SellerContext';

export const SecureDispatchModal = ({ modal, onClose, onOrdersUpdate }) => {
  const [dispatchOtp, setDispatchOtp] = useState('');
  const [otpId, setOtpId] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const otpTriggeredRef = useRef(false);
  const countdownIntervalRef = useRef(null);

  // Consume SellerContext directly to get sellerId (used as PARTY_ID)
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;

  const {
    sendDispatchOtp,
    verifyDispatchOtp,
    dispatchLoading,
    otpError,
    setOtpError,
  } = useOTPVerification();

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownIntervalRef.current && resendCountdown === 0) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [resendCountdown]);

  const handleSendOtp = useCallback(async () => {
    if (!sellerId) {
      setOtpError('Seller ID missing from session.');
      console.error('❌ Seller ID not found:', sellerId);
      return;
    }

    if (!modal.jreMobile) {
      setOtpError('JRE mobile number not available.');
      console.error('❌ JRE Mobile not found:', modal.jreMobile);
      return;
    }

    console.log('🔐 Sending OTP with params:', {
      jreMobile: modal.jreMobile,
      sellerId: sellerId,
    });

    const result = await sendDispatchOtp(modal.jreMobile, sellerId);
    if (result.success) {
      setOtpId(result.otpId);
      setResendCountdown(300); // 5 minutes = 300 seconds
      console.log('✅ OTP sent successfully with otpId:', result.otpId);
    } else {
      console.error('❌ Failed to send OTP');
    }
  }, [sellerId, modal, sendDispatchOtp, setOtpError]);

  // Auto-send OTP on open
  useEffect(() => {
    if (modal && sellerId && !otpTriggeredRef.current) {
      otpTriggeredRef.current = true;
      console.log('📱 Auto-sending OTP with:', {
        jreMobile: modal.jreMobile,
        sellerId: sellerId,
        groupData: modal.groupData,
      });
      handleSendOtp();
    }
  }, [modal, sellerId, handleSendOtp]);

  if (!modal) return null;

  const handleVerify = async () => {
    // Create modal data with otpId
    const modalWithOtpId = {
      ...modal,
      otpId: otpId,
    };

    console.log('🔍 Verifying OTP with params:', {
      modal: modalWithOtpId,
      dispatchOtp: dispatchOtp,
      sellerId: sellerId,
      groupData: modal.groupData,
    });

    const success = await verifyDispatchOtp(modalWithOtpId, dispatchOtp, sellerId);
    if (success) {
      console.log('✅ OTP verified successfully, updating orders');
      onOrdersUpdate((prev) =>
        prev.filter(
          (order) =>
            !(
              order.jreId === modal.groupData.jreId &&
              order.operatorId === modal.groupData.operatorId &&
              order.orderStatus === 'Assigned'
            )
        )
      );
      handleClose();
    } else {
      console.error('❌ OTP verification failed');
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setDispatchOtp(val);
    setOtpError('');
  };

  const handleClose = () => {
    setDispatchOtp('');
    setOtpId(null);
    setResendCountdown(0);
    otpTriggeredRef.current = false;
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="modal-overlay active">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Verify Dispatch OTP</h3>
        </div>

        <div className="modal-body">
          <p className="otp-instruction">
            Enter the 6-digit OTP sent to JRE: <strong>+91 {modal.jreMobile}</strong>
          </p>

          <div className="otp-input-section">
            <input
              key="dispatch-otp-input"
              type="text"
              value={dispatchOtp}
              onChange={handleOtpChange}
              placeholder="000000"
              maxLength={6}
              className={`otp-input ${otpError ? 'error' : ''}`}
              disabled={dispatchLoading}
              autoFocus
            />
            {otpError && <div className="error-message">{otpError}</div>}
          </div>

          <div className="resend-container" style={{ marginTop: '10px' }}>
            {resendCountdown > 0 ? (
              <span
                style={{
                  color: '#999',
                  cursor: 'not-allowed',
                  fontSize: '14px',
                }}
              >
                Resend OTP in: <strong>{formatTime(resendCountdown)}</strong>
              </span>
            ) : (
              <span
                onClick={!dispatchLoading ? handleSendOtp : undefined}
                style={{
                  color: dispatchLoading ? '#ccc' : '#007bff',
                  cursor: dispatchLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                }}
              >
                {dispatchLoading ? 'Sending...' : 'Resend OTP'}
              </span>
            )}
          </div>

          <div className="dispatch-info" style={{ marginTop: '15px' }}>
            <p><strong>Dispatch Summary:</strong></p>
            <p>Seller ID (PARTY_ID): {sellerId || 'N/A'}</p>
            <p>OTP ID: {otpId ? otpId.substring(0, 20) + '...' : 'Pending'}</p>
            <p>Operator: {modal.groupData.operatorName || modal.groupData.operatorId}</p>
            {modal.groupData.isCourier || modal.groupData.courierId ? (
              <p>
                Courier:{" "}
                {modal.groupData.courierName && modal.groupData.courierBranchName
                  ? `${modal.groupData.courierName} — ${modal.groupData.courierBranchName}`
                  : modal.groupData.courierBranchName || modal.groupData.courierName || "Courier assigned"}
                {(modal.groupData.courierContactPerson || modal.groupData.courierContactNumber) && (
                  <>
                    {" "}(
                    {modal.groupData.courierContactPerson ? `${modal.groupData.courierContactPerson}: ` : ""}
                    {modal.groupData.courierContactNumber || ""})
                  </>
                )}
              </p>
            ) : (
              modal.groupData.jreId && modal.groupData.jreId !== 'No JRE' && (
                <p>JRE: {modal.groupData.jreName || modal.groupData.jreId}</p>
              )
            )}
            <p>Packets: {modal.groupData.orders.length}</p>
            <p>Items: {modal.groupData.totalItems}</p>
          </div>
        </div>

        <div className="modal-actions">
          <button
            onClick={handleClose}
            disabled={dispatchLoading}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={dispatchLoading || dispatchOtp.length !== 6}
            className="btn btn-primary"
          >
            {dispatchLoading ? 'Verifying...' : 'Confirm Pickup'}
          </button>
        </div>
      </div>
    </div>
  );
};